// S8 — FLOOR DEVICES (spec 04 §4 S8, §5 row 26, §2.16 — slice 04-B-3).
//
// The registry with its telemetry: last seen, last sync, the DEVICE-reported
// pending-outbox hint (a hint, never a server-derived truth — the schema says
// so), and the three actions the endpoint offers: register, rename, retire.
// There is deliberately NO delete and no "deactivate + forget": operation
// events reference the device forever, so retire is `active: false` and the
// row stays. `device_code` is printed on the physical device and immutable.
//
// Roles: facility_admin (+ group_admin) via `production.floor.manage`.

import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { CheckboxField, Field } from "../../../../components/field";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { useTerm } from "../../../onboarding/hooks/terminology";
import {
  useCreateFloorDevice,
  useFacilities,
  useFloorDevices,
  useUpdateFloorDevice,
  type FloorDevice,
} from "../hooks/production-queries";
import { useIsDesktop } from "../hooks/use-is-desktop";
import { formatTimestamp } from "../lib/format";

export function DevicesScreen() {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const navigate = useNavigate();
  const [showRetired, setShowRetired] = useState(false);
  const [registering, setRegistering] = useState(false);
  const isDesktop = useIsDesktop();

  // ROW STATE LIVES HERE, one row at a time. It used to live inside a
  // per-row `<tr>` component, which the §3.3 primitive now owns; it also spans
  // TWO cells (renaming swaps the Name cell for an input AND hides the Rename
  // button), so it could never have been the state of one cell. One open
  // rename and one open retire-confirmation at a time is also the honest
  // reading of both: each is a decision about a single device.
  const update = useUpdateFloorDevice();
  const [renamingId, setRenamingId] = useState<string | undefined>(undefined);
  const [nameDraft, setNameDraft] = useState("");
  const [confirmRetireId, setConfirmRetireId] = useState<string | undefined>(undefined);

  const list = useFloorDevices(showRetired ? {} : { is_active: "true" });
  const facilities = useFacilities();

  useEffect(() => {
    if (list.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [list.error, navigate]);

  const facilityName = new Map<string, string>();
  for (const facility of facilities.data?.items ?? []) facilityName.set(facility.id, facility.name);

  const rows = list.data?.items ?? [];

  const columns: DataTableColumn<FloorDevice>[] = [
    {
      key: "device_code",
      header: t("production.devices.col.code"),
      id: true,
      render: (device) => <code>{device.device_code}</code>,
    },
    {
      key: "name",
      header: t("production.devices.col.name"),
      render: (device) =>
        renamingId === device.id ? (
          <span className="device-rename">
            <input
              className="ds-input"
              value={nameDraft}
              maxLength={120}
              data-testid="device-rename-input"
              onChange={(event) => {
                setNameDraft(event.target.value);
              }}
            />
            <Button
              data-testid="device-rename-save"
              busy={update.isPending}
              disabled={nameDraft.trim().length === 0}
              onClick={() => {
                update.mutate(
                  { id: device.id, body: { name: nameDraft.trim() } },
                  {
                    onSuccess: () => {
                      setRenamingId(undefined);
                    },
                  },
                );
              }}
            >
              {t("production.common.save")}
            </Button>
            <Button
              onClick={() => {
                setRenamingId(undefined);
              }}
            >
              {t("production.common.cancel")}
            </Button>
          </span>
        ) : (
          device.name
        ),
    },
    {
      key: "facility_id",
      header: term("facility"),
      render: (device) =>
        facilityName.get(device.facility_id) ?? t("production.common.notAvailable"),
    },
    {
      key: "last_seen",
      header: t("production.devices.col.lastSeen"),
      age: true,
      // lib/datetime's ONE formatter (F9): a bare `toLocaleString()` reads the
      // BROWSER's locale, not the app's, so this column printed
      // "8/10/2026, 1:33:23 PM" beside Russian dates on the same screen.
      render: (device) =>
        device.last_sync_at !== null
          ? formatTimestamp(device.last_sync_at, i18n.language)
          : device.last_seen_at !== null
            ? formatTimestamp(device.last_seen_at, i18n.language)
            : t("production.devices.neverSeen"),
    },
    {
      key: "pending_outbox_hint",
      header: t("production.devices.col.pending"),
      // DEVICE-reported at last sync — a hint, never server truth (§2.16).
      render: (device) =>
        t("production.devices.pendingHint", { count: device.pending_outbox_hint }),
    },
    {
      key: "active",
      header: t("production.devices.col.status"),
      render: (device) =>
        device.active ? t("production.devices.active") : t("production.devices.retired"),
    },
    {
      key: "actions",
      header: t("production.common.actions"),
      actions: true,
      render: (device) =>
        device.active ? (
          <div className="wo-transitions">
            {renamingId !== device.id ? (
              <Button
                data-testid="device-rename"
                onClick={() => {
                  setNameDraft(device.name);
                  setRenamingId(device.id);
                }}
              >
                {t("production.devices.rename")}
              </Button>
            ) : null}
            {confirmRetireId === device.id ? (
              <>
                <Button
                  data-testid="device-retire-confirm"
                  busy={update.isPending}
                  onClick={() => {
                    update.mutate(
                      { id: device.id, body: { active: false } },
                      {
                        onSuccess: () => {
                          setConfirmRetireId(undefined);
                        },
                      },
                    );
                  }}
                >
                  {t("production.devices.retireConfirm")}
                </Button>
                <Button
                  onClick={() => {
                    setConfirmRetireId(undefined);
                  }}
                >
                  {t("production.common.cancel")}
                </Button>
              </>
            ) : (
              <Button
                data-testid="device-retire"
                onClick={() => {
                  setConfirmRetireId(device.id);
                }}
              >
                {t("production.devices.retire")}
              </Button>
            )}
          </div>
        ) : null,
    },
  ];

  return (
    <section className="devices-list" data-testid="devices-screen">
      <PageHeader
        title={t("production.devices.title")}
        actions={
          <span className="wo-transitions">
            <Button
              variant="primary"
              data-testid="device-register-open"
              onClick={() => {
                setRegistering(true);
              }}
            >
              {t("production.devices.register")}
            </Button>
          </span>
        }
      />

      <p className="production-mobile-hint">{t("production.devices.mobileHint")}</p>

      <div className="filter-bar">
        <CheckboxField
          label={t("production.devices.showRetired")}
          checked={showRetired}
          data-testid="devices-show-retired"
          onChange={setShowRetired}
        />
      </div>

      {registering ? (
        <RegisterForm
          facilities={facilities.data?.items ?? []}
          onClose={() => {
            setRegistering(false);
          }}
        />
      ) : null}

      {list.isPending ? (
        <TableSkeleton rows={4} cols={6} label={t("production.common.loading")} />
      ) : null}

      {list.error && !(list.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("production.devices.error")}
          detail={list.error.message}
          retryLabel={t("production.common.retry")}
          onRetry={() => void list.refetch()}
          requestId={requestIdOf(list.error)}
          requestIdLabel={t("production.common.requestId")}
        />
      ) : null}

      {list.data !== undefined && rows.length === 0 ? (
        <EmptyState
          title={t("production.devices.emptyTitle")}
          body={t("production.devices.emptyBody")}
          // THE CTA IS DESKTOP-ONLY, like the header opener beside it. §4's
          // mobile rule removes `.wo-transitions` below 768 px — and the
          // register form's SUBMIT lives in one — so on a phone this CTA
          // opened a form that could not be submitted: the empty state's only
          // exit was a dead end (builder report, 2026-08-12). S1's empty state
          // has taken `isDesktop` for exactly this reason since 04-A.
          {...(isDesktop
            ? {
                ctaLabel: t("production.devices.register"),
                onCta: () => {
                  setRegistering(true);
                },
              }
            : {})}
        />
      ) : null}

      {rows.length > 0 ? (
        <DataTable
          data-testid="devices-table"
          columns={columns}
          rows={rows}
          rowKey={(device) => device.id}
          empty={null}
          rowTestId="device-row"
          rowProps={(device) => ({ "data-active": device.active ? "true" : "false" })}
        />
      ) : null}
    </section>
  );
}

function RegisterForm({
  facilities,
  onClose,
}: {
  facilities: readonly { id: string; name: string }[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { term } = useTerm();
  const create = useCreateFloorDevice();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [facilityId, setFacilityId] = useState("");
  const effectiveFacility = facilityId || (facilities[0]?.id ?? "");
  const error = create.error instanceof RequestError ? create.error : undefined;

  const submit = (): void => {
    if (code.trim().length === 0 || name.trim().length === 0 || effectiveFacility.length === 0) {
      return;
    }
    create.mutate(
      { device_code: code.trim(), name: name.trim(), facility_id: effectiveFacility },
      { onSuccess: onClose },
    );
  };

  return (
    <div className="device-register-form" data-testid="device-register-form">
      {/* The §3.10 Field grammar — label wraps control, same association the
          bare <label>s carried; the testids stay on the controls. */}
      <Field label={t("production.devices.col.code")}>
        <input
          value={code}
          maxLength={64}
          data-testid="device-code-input"
          onChange={(event) => {
            setCode(event.target.value);
          }}
        />
      </Field>
      <p className="device-code-note">{t("production.devices.codeNote")}</p>
      <Field label={t("production.devices.col.name")}>
        <input
          value={name}
          maxLength={120}
          data-testid="device-name-input"
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
      </Field>
      <Field label={term("facility")}>
        <select
          value={effectiveFacility}
          data-testid="device-facility-select"
          onChange={(event) => {
            setFacilityId(event.target.value);
          }}
        >
          {facilities.map((facility) => (
            <option key={facility.id} value={facility.id}>
              {facility.name}
            </option>
          ))}
        </select>
      </Field>
      <div className="wo-transitions">
        <Button
          data-testid="device-register-submit"
          busy={create.isPending}
          busyLabel={t("production.common.saving")}
          disabled={code.trim().length === 0 || name.trim().length === 0}
          onClick={submit}
        >
          {t("production.devices.register")}
        </Button>
        <Button onClick={onClose}>{t("production.common.cancel")}</Button>
      </div>
      {error !== undefined ? (
        <p className="conflict-form-error" role="alert" data-testid="device-register-error">
          {error.problem?.title ?? t("production.devices.registerFailed")}
          {error.requestId !== undefined ? <code> {error.requestId}</code> : null}
        </p>
      ) : null}
    </div>
  );
}
